/**
 * @file userService.ts
 * @description User Context Provider with TypeScript - Task 18.6
 *
 * Provides React Context for user authentication state with:
 * - Typed UserContextValue interface
 * - JWT token management (cookie + localStorage)
 * - Login function with typed AuthResponse
 * - Auth verification on mount
 *
 * Reference: .taskmaster/docs/research/task-18-api-services-typescript-migration.md
 * Section 3.6
 */

import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import type { AxiosError, InternalAxiosRequestConfig } from 'axios';
import Cookies from 'js-cookie';

import apiClient from './apiClient';
import { postAuthActions, checkUserDetails, logout } from './authService';
import { getCookieDomainFromUrl } from '../utils';
import type { UserContextValue, User, AuthResponse } from '../types/user';

const domain = getCookieDomainFromUrl(process.env.REACT_APP_LANDING_PAGE);

/**
 * User Context with typed value or undefined before provider mount
 */
const UserContext = createContext<UserContextValue | undefined>(undefined);

/**
 * Hook to access UserContext value
 *
 * @throws Error if used outside UserProvider
 * @returns UserContextValue with auth state and methods
 */
export const useUser = (): UserContextValue => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

/**
 * Props for UserProvider component
 */
interface UserProviderProps {
  children: ReactNode;
}

/**
 * User Context Provider component
 *
 * Manages authentication state and provides:
 * - isLoggedIn: boolean auth status
 * - userId: current user ID (from userDetails or localStorage)
 * - jwt: current JWT token
 * - userDetails: full user object after verification
 * - login: function to authenticate user
 * - getUserDetailsWithTournaments: legacy method to get user details
 */
export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const [jwt, setJwt] = useState<string | null>(
    () => Cookies.get('jwt') || localStorage.getItem('jwt')
  );
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(!!jwt);
  const [userDetails, setUserDetails] = useState<User | null>(null);

  /**
   * Returns user details (legacy method for backward compatibility)
   */
  const getUserDetailsWithTournaments = async (): Promise<User | null> => {
    if (!jwt) {
      return null;
    }

    try {
      const userDetailsData = await checkUserDetails();
      setUserDetails(userDetailsData as User);
      return userDetailsData as User;
    } catch (error) {
      console.error('Failed to fetch user details with tournaments:', error);
      return userDetails;
    }
  };

  /**
   * Verify JWT on mount and load user details
   */
  useEffect(() => {
    const verifyAuthStatus = async (): Promise<void> => {
      if (jwt) {
        try {
          const userDetailsData = await checkUserDetails();
          setIsAuthenticated(true);
          setUserDetails(userDetailsData as User);
        } catch (error) {
          console.error('JWT validation failed:', error);
          setIsAuthenticated(false);
          logout();
          setJwt(null);
        }
      } else {
        setIsAuthenticated(false);
      }
    };

    verifyAuthStatus();
  }, []);

  /**
   * Request interceptor to inject JWT into Authorization header
   */
  useEffect(() => {
    const requestInterceptor = apiClient.interceptors.request.use(
      (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
        if (jwt && config.headers) {
          config.headers.Authorization = `Bearer ${jwt}`;
        }
        return config;
      },
      (error: AxiosError): Promise<never> => Promise.reject(error)
    );

    return () => {
      apiClient.interceptors.request.eject(requestInterceptor);
    };
  }, [jwt]);

  /**
   * Login user with identifier (username/email) and password
   *
   * @param identifier - Username or email
   * @param password - User password
   * @param callback - Optional callback with token after successful login
   * @returns AuthResponse with jwt and user data
   */
  const login = async (
    identifier: string,
    password: string,
    callback?: (token: string) => void
  ): Promise<AuthResponse> => {
    try {
      const response = await apiClient.post<AuthResponse>('/auth/local', {
        identifier,
        password,
      });

      if (response.status === 200) {
        const token = response.data.jwt;
        const userData = response.data.user;

        setJwt(token);
        localStorage.setItem('userId', String(userData.id));
        localStorage.setItem('jwt', token);
        localStorage.setItem('username', userData.username);
        localStorage.setItem('email', userData.email);

        // Setting cookies
        Cookies.set('userId', String(userData.id), {
          expires: 7,
          secure: true,
          sameSite: 'none',
          domain,
        });
        Cookies.set('jwt', token, {
          expires: 7,
          secure: true,
          sameSite: 'none',
          domain,
        });
        Cookies.set('username', userData.username, {
          expires: 7,
          secure: true,
          sameSite: 'none',
          domain,
        });
        Cookies.set('email', userData.email, {
          expires: 7,
          secure: true,
          sameSite: 'none',
          domain,
        });

        await postAuthActions();

        if (callback) {
          callback(token);
        }
      }

      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      throw axiosError.response ? axiosError.response.data : axiosError.message;
    }
  };

  // Get userId from userDetails or localStorage as fallback (Task 37.4)
  const userId: string | number | null =
    userDetails?.id ?? localStorage.getItem('userId');

  const value: UserContextValue = {
    getUserDetailsWithTournaments,
    isLoggedIn: isAuthenticated,
    userId,
    jwt,
    userDetails,
    login,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};
